import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Switch,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import { format, subDays } from 'date-fns'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import FormProvider from '../../../common/hook-form/FormProvider'
import RHFSelectField from '../../../common/hook-form/RHFSelectField'
import RHFTextField from '../../../common/hook-form/RHFTextField'
import RHFDateRangePickerField from '../../../common/hook-form/RHFDateRangePickerField'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { apiConstants, get, post } from '../../../common/constants'
import MUIModal from '../../MuiModal'
import { MerchantFilterCached } from '../../../common/ProshipFormComponents'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../../redux/commonApiCalls'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'

const PAGE_SIZE = 20

function formatDateTime(val) {
  if (!val) return '—'
  try { return format(new Date(val), 'dd MMM yyyy, HH:mm:ss') } catch { return val }
}

export default function AuditLogsTab() {
  const { enqueueSnackbar } = useSnackbar()

  // Schema state
  const [entityOptions, setEntityOptions] = useState([])
  const [schemaLoading, setSchemaLoading] = useState(true)

  // Results state
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  // Cursor-based pagination
  const [cursorStack, setCursorStack] = useState([])
  const [currentCursor, setCurrentCursor] = useState(null)
  const [hasNextPage, setHasNextPage] = useState(false)
  const [lastFormData, setLastFormData] = useState(null)

  // Detail modal
  const [detailRow, setDetailRow] = useState(null)

  const methods = useForm({
    defaultValues: {
      auditEntity: '',
      entityId: '',
      merchant: null,
      fromDate: subDays(new Date(), 7),
      toDate: new Date(),
      includeDetails: false,
    },
  })

  const { handleSubmit, control, reset, watch } = methods
  const auditEntity = watch('auditEntity')
  const isMerchantEntity = ['MERCHANT', 'MERCHANT_CONFIG'].includes(auditEntity)

  // Merchant lookup for column display
  const { data: merchantsRaw = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const merchantMap = {}
  merchantsRaw.forEach((m) => { merchantMap[m.id] = m.companyName ? `${m.name} (${m.companyName})` : m.name })


  const handleReset = () => {
    reset({ auditEntity: '', entityId: '', merchant: null, fromDate: subDays(new Date(), 7), toDate: new Date(), includeDetails: false })
    setRows([])
    setSubmitted(false)
    setCursorStack([])
    setCurrentCursor(null)
    setLastFormData(null)
  }

  // Fetch schema on mount
  useEffect(() => {
    setSchemaLoading(true)
    get(apiConstants.AUDIT_SCHEMA)
      .then((res) => {
        const fields = res?.data?.fields || []
        const entityField = fields.find((f) => f.name === 'auditEntity')
        if (entityField?.values) {
          const toLabel = (v) => v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
          setEntityOptions(entityField.values.map((v) => ({ value: v, label: toLabel(v) })))
        }
      })
      .catch(() => {
        enqueueSnackbar('Failed to load audit schema', { variant: 'error' })
      })
      .finally(() => setSchemaLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const buildPayload = (formData, cursor = null) => {
    const payload = { limit: PAGE_SIZE, includeDetails: formData.includeDetails || false }
    if (formData.auditEntity) payload.auditEntity = formData.auditEntity
    const isMerchant = ['MERCHANT', 'MERCHANT_CONFIG'].includes(formData.auditEntity)
    if (isMerchant && formData.merchant) payload.entityId = [formData.merchant]
    else if (!isMerchant && formData.entityId?.trim()) payload.entityId = [formData.entityId.trim()]
    if (formData.fromDate) payload.fromDate = format(new Date(formData.fromDate), 'dd-MM-yyyy')
    if (formData.toDate) payload.toDate = format(new Date(formData.toDate), 'dd-MM-yyyy')
    if (formData.merchant) payload.merchantId = formData.merchant
    if (cursor?.cursorAuditTime) payload.cursorAuditTime = cursor.cursorAuditTime
    if (cursor?.cursorId) payload.cursorId = cursor.cursorId
    return payload
  }

  const fetchResults = (payload) => {
    setLoading(true)
    post(apiConstants.AUDIT_SEARCH, payload)
      .then((res) => {
        const raw = res?.data
        const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []
        setRows(list.map((r, i) => ({
          ...r,
          id: r.id || r._id || i,
          // Stringify nested objects to avoid DataGrid rendering issues
          oldValueStr: r.oldValue ? JSON.stringify(r.oldValue, null, 2) : null,
          newValueStr: r.newValue ? JSON.stringify(r.newValue, null, 2) : null,
        })))
        setHasNextPage(list.length >= payload.limit)
        setSubmitted(true)
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || 'Failed to fetch audit logs'
        enqueueSnackbar(msg, { variant: 'error' })
      })
      .finally(() => setLoading(false))
  }

  const onSubmit = (formData) => {
    setLastFormData(formData)
    setCursorStack([])
    setCurrentCursor(null)
    fetchResults(buildPayload(formData, null))
  }

  const handleNextPage = () => {
    if (!rows.length || !lastFormData) return
    const lastItem = rows[rows.length - 1]
    const nextCursor = { cursorAuditTime: lastItem.auditTime, cursorId: lastItem.id || lastItem._id }
    setCursorStack((prev) => [...prev, currentCursor])
    setCurrentCursor(nextCursor)
    fetchResults(buildPayload(lastFormData, nextCursor))
  }

  const handlePrevPage = () => {
    if (!cursorStack.length || !lastFormData) return
    const newStack = [...cursorStack]
    const prevCursor = newStack.pop()
    setCursorStack(newStack)
    setCurrentCursor(prevCursor)
    fetchResults(buildPayload(lastFormData, prevCursor))
  }

  const columns = [
    {
      field: 'auditTime',
      headerName: 'Time',
      flex: 1.5,
      renderCell: (p) => <Typography variant="body2">{formatDateTime(p.value)}</Typography>,
    },
    {
      field: 'auditEntity',
      headerName: 'Entity',
      flex: 1.2,
      renderCell: (p) =>
        p.value ? (
          <Chip label={p.value} size="small" color="primary" variant="outlined" />
        ) : (
          <Typography variant="body2">—</Typography>
        ),
    },
    {
      field: 'entityId',
      headerName: 'Entity ID',
      flex: 1.5,
      renderCell: (p) => {
        const name = merchantMap[p.value]
        return (
          <Tooltip title={p.value || ''}>
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
              {name || p.value || '—'}
            </Typography>
          </Tooltip>
        )
      },
    },
    {
      field: 'attributeName',
      headerName: 'Attribute',
      flex: 1.2,
      renderCell: (p) =>
        p.value ? (
          <Chip label={p.value} size="small" variant="outlined" />
        ) : (
          <Typography variant="body2">—</Typography>
        ),
    },
    {
      field: 'userId',
      headerName: 'Performed By',
      flex: 1.5,
      renderCell: (p) => <Typography variant="body2">{p.value || '—'}</Typography>,
    },
    {
      field: 'trackingId',
      headerName: 'Tracking ID',
      flex: 1,
      renderCell: (p) => (
        <Tooltip title={p.value || ''}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
            {p.value ? p.value.split('-')[0] + '…' : '—'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.5,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title="View details">
          <IconButton size="small" onClick={() => setDetailRow(p.row)}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  return (
    <Box>
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider' }}>
          <FilterAltOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>Search Filters</Typography>
        </Box>
        <CardContent>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <RHFSelectField
                  name="auditEntity"
                  label="Entity Type"
                  options={schemaLoading ? [] : entityOptions}
                  width="100%"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                {isMerchantEntity
                  ? <MerchantFilterCached />
                  : <RHFTextField name="entityId" label="Entity ID" placeholder="e.g. ObjectId" />
                }
              </Grid>
              <Grid item xs={12} md={3}>
                <RHFDateRangePickerField label="Date Range" />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="includeDetails"
                  control={control}
                  render={({ field }) => (
                    <Box
                      onClick={() => field.onChange(!field.value)}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 1.5, height: 40, borderRadius: 1, cursor: 'pointer',
                        border: '1px solid', borderColor: field.value ? 'primary.main' : 'rgba(0,0,0,0.23)',
                        '&:hover': { borderColor: 'text.primary' },
                      }}
                    >
                      <Typography variant="body2" color={field.value ? 'primary.main' : 'text.secondary'} sx={{ userSelect: 'none' }}>
                        Include Change Details
                      </Typography>
                      <Switch checked={field.value} size="small" color="primary" onChange={() => { }} />
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button type="submit" variant="contained" size="medium" disabled={loading} startIcon={<SearchOutlinedIcon />}>
                    Search
                  </Button>
                  <Button type="button" variant="outlined" size="medium" onClick={handleReset} startIcon={<RestartAltOutlinedIcon />}>
                    Reset
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </FormProvider>
        </CardContent>
      </Card>

      {submitted && (
        <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: 'none' }}>
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
            <Typography variant="subtitle2" fontWeight={600}>Results</Typography>
            <Chip label={`${rows.length} records`} size="small" color="primary" variant="outlined" />
          </Box>
          {rows.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <InboxOutlinedIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>No records found</Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>Try adjusting your filters and search again</Typography>
            </Box>
          ) : (
            <CardContent sx={{ px: 0, pb: '0 !important' }}>
              <ProzoDataGrid
                rows={rows}
                columns={columns}
                rowCount={rows.length}
                hideFooterPagination
                disableColumnMenu
                disableColumnFilter
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', px: 2, py: 1, gap: 1 }}>
                <Button
                  size="small"
                  startIcon={<NavigateBeforeIcon />}
                  onClick={handlePrevPage}
                  disabled={cursorStack.length === 0 || loading}
                  variant="outlined"
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  endIcon={<NavigateNextIcon />}
                  onClick={handleNextPage}
                  disabled={!hasNextPage || loading}
                  variant="outlined"
                >
                  Next
                </Button>
              </Box>
            </CardContent>
          )}
        </Card>
      )}

      {/* Detail Modal */}
      <MUIModal
        open={Boolean(detailRow)}
        handleClose={() => setDetailRow(null)}
        title="Audit Log Detail"
        maxWidth="md"
      >
        {detailRow && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Time</Typography>
                <Typography variant="body2">{formatDateTime(detailRow.auditTime)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Entity</Typography>
                <Typography variant="body2">{detailRow.auditEntity || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Entity ID</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{detailRow.entityId || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Attribute</Typography>
                <Typography variant="body2">{detailRow.attributeName || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Performed By</Typography>
                <Typography variant="body2">{detailRow.userId || '—'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Tracking ID</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{detailRow.trackingId || '—'}</Typography>
              </Grid>
              {detailRow.oldValueStr && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Old Value</Typography>
                  <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontSize: '11px', overflowX: 'auto', maxHeight: 240 }}>
                    {detailRow.oldValueStr}
                  </Box>
                </Grid>
              )}
              {detailRow.newValueStr && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 0.5 }}>New Value</Typography>
                  <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontSize: '11px', overflowX: 'auto', maxHeight: 240 }}>
                    {detailRow.newValueStr}
                  </Box>
                </Grid>
              )}
              {!detailRow.oldValueStr && !detailRow.newValueStr && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Enable "Include Change Details" to see old/new values.</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </MUIModal>
    </Box>
  )
}
